import {LOGIN_REDIRECT_URL,
        AUTH_ENDPOINT,
        CSRF_ENDPOINT } from './externalUrls'

export type AuthUser = {
  username: string | null;
  src: string | null;
  /** Créditos (mensajes) disponibles. `null` = todavía desconocido. */
  availableMessages: number | null;
};

export let currentUser = $state<AuthUser>({ username: null, src: null, availableMessages: null });

let balanceVersion = 0;
let sessionRequestVersion = 0;

/** Actualiza el saldo de créditos (desde /session/ o desde el WebSocket). */
export function setAvailableMessages(value: number | null): void {
  balanceVersion += 1;
  currentUser.availableMessages =
    typeof value === 'number' && Number.isFinite(value) ? value : null;
}

let authCheckPromise: Promise<boolean> | null = null;
let authInitializationPromise: Promise<boolean> | null = null;

/**
 * Fetch CSRF token from backend.
 * This ensures Django sets the csrftoken cookie.
 */
export async function fetchCsrfToken(): Promise<void> {
  try {
    await fetch(CSRF_ENDPOINT, {
      credentials: "include",
      method: 'GET'
    });
  } catch (e) {
    console.error('Error fetching CSRF token:', e);
  }
}

async function performAuthCheck(timeoutMs: number): Promise<boolean> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  const balanceAtStart = balanceVersion;
  const requestVersion = ++sessionRequestVersion;

  try {
    const res = await fetch(AUTH_ENDPOINT, {
      credentials: "include",
      method: 'GET',
      signal: controller.signal
    });

    clearTimeout(id);

    if (!res.ok) {
      // 404 u otro error => sin sesión
      currentUser.username = null;
      currentUser.src = null;
      setAvailableMessages(null);
      window.location.href = LOGIN_REDIRECT_URL;
      return false;
    }

    const data = (await res.json()) as {
      username: string;
      src: string;
      available_messages?: number;
    };
    currentUser.username = data.username;
    currentUser.src = data.src;
    // El saldo de /session/ puede ser anterior a un descuento recibido por WS.
    if (balanceVersion === balanceAtStart && requestVersion === sessionRequestVersion) {
      setAvailableMessages(data.available_messages ?? null);
    }
    return true;
  } catch (e) {
    console.error('Error en /auth:', e);
    clearTimeout(id);
    currentUser.username = null;
    currentUser.src = null;
    setAvailableMessages(null);
    window.location.href = LOGIN_REDIRECT_URL;
    return false;
  }
}

export function checkAuth(timeoutMs = 15000): Promise<boolean> {
  if (!authCheckPromise) {
    authCheckPromise = performAuthCheck(timeoutMs).finally(() => {
      authCheckPromise = null;
    });
  }
  return authCheckPromise;
}

/** One shared initialization barrier for components that need an authenticated API. */
export function initializeAuth(): Promise<boolean> {
  if (!authInitializationPromise) {
    authInitializationPromise = fetchCsrfToken().then(() => checkAuth());
  }
  return authInitializationPromise;
}

/**
 * Relee la sesión (usuario, avatar y créditos) sin redirigir ante un fallo.
 *
 * Se usa para reconciliar el saldo cuando no hay un frame de WebSocket: al
 * volver a la pestaña después de comprar créditos o activar un plan.
 */
export async function refreshSessionInfo(): Promise<boolean> {
  const balanceAtStart = balanceVersion;
  const requestVersion = ++sessionRequestVersion;
  try {
    const res = await fetch(AUTH_ENDPOINT, {
      credentials: 'include',
      method: 'GET'
    });
    if (!res.ok) return false;

    const data = (await res.json()) as {
      username?: string;
      src?: string;
      available_messages?: number;
    };
    if (typeof data.username === 'string') currentUser.username = data.username;
    currentUser.src = data.src ?? null;
    if (typeof data.available_messages === 'number' &&
        balanceVersion === balanceAtStart && requestVersion === sessionRequestVersion) {
      setAvailableMessages(data.available_messages);
    }
    return true;
  } catch (e) {
    console.warn('No se pudo refrescar la sesión:', e);
    return false;
  }
}
